/* eslint-disable @typescript-eslint/no-non-null-assertion */
import {
  MutableRefObject,
  useCallback,
  useMemo,
  useRef,
  useState,
  useContext,
} from 'react';
import { AUTO_DETECT } from 'src/constants/languages';
import {
  AudioConfig,
  AutoDetectSourceLanguageConfig,
  Recognizer,
  ResultReason,
  SpeechConfig,
  SpeechRecognitionEventArgs,
  SpeechRecognizer,
} from 'microsoft-cognitiveservices-speech-sdk';
import AudioContext from 'src/contexts/AudioContext';
import { translateText } from 'src/utils';
import { logAction } from 'src/utils/analytics';
import { useLazyGetSpeechTokenQuery } from 'src/store/services';
import { useSession } from 'src/hooks';
import log from 'src/utils/logger';
import logger from 'src/utils/logger';

const PAUSE_TIMEOUT = 5000;

type useSpeechRecognizerReturnValue = {
  recordInProgressRef: MutableRefObject<boolean> | undefined;
  voiceDetected: boolean;
  stopSpeechRecognizing: () => Promise<void>;
  startSpeechRecognizing: (defaultValue?: string) => Promise<void>;
};

type Props = {
  onRecognizing: (value: string) => void;
  sourceLanguage?: string;
};

const autoDetectLanguages = [
  'en-US',
  'ar-SA',
  'es-MX',
  'fr-FR',
  'hi-IN',
  'ja-JP',
  'zh-CN',
  'de-DE',
  'tr-TR',
  'uk-UA',
];

export const useSpeechRecognizer = ({
  onRecognizing,
  sourceLanguage = 'en-US',
}: Props): useSpeechRecognizerReturnValue => {
  // recordInProgressRef is needed for sync state update so that it can be used in async callback.
  const recordInProgressRef = useRef(false);
  const recognizer = useRef<SpeechRecognizer>();
  const audioConfig = useRef<AudioConfig>();
  const mediaStream = useRef<MediaStream>();
  const accumulatedSpeechToTextValue = useRef<string>('');

  const [voiceDetected, setVoiceDetected] = useState<boolean>(false);

  const { setDetectedLanguage, setRecordInProgress } = useContext(AudioContext);
  const [fetchSpeechToken] = useLazyGetSpeechTokenQuery();
  const { appUser } = useSession();

  const initializeRecognizer = useCallback(async () => {
    const result = await fetchSpeechToken(appUser.user_id);

    if (!result.data) {
      return;
    }
    const speechConfig = SpeechConfig.fromAuthorizationToken(
      result.data.token,
      result.data.region,
    );

    mediaStream.current = await navigator.mediaDevices.getUserMedia({
      audio: true,
    });

    audioConfig.current = AudioConfig.fromStreamInput(mediaStream.current);

    speechConfig.setProperty(
      'Speech_SegmentationSilenceTimeoutMs',
      `${PAUSE_TIMEOUT}`,
    );
    speechConfig.setProperty(
      'SpeechServiceConnection_LanguageIdMode',
      `Continuous`,
    );

    const audioConfiguration = AudioConfig.fromStreamInput(mediaStream.current);

    const isAutoDetect = sourceLanguage === AUTO_DETECT;

    if (isAutoDetect) {
      const autoDetectSourceLanguageConfig =
        AutoDetectSourceLanguageConfig.fromLanguages(autoDetectLanguages);

      recognizer.current = SpeechRecognizer.FromConfig(
        speechConfig,
        autoDetectSourceLanguageConfig,
        audioConfiguration,
      );
    } else {
      speechConfig.speechRecognitionLanguage = sourceLanguage;
      recognizer.current = new SpeechRecognizer(
        speechConfig,
        audioConfig.current,
      );
    }
  }, [sourceLanguage, fetchSpeechToken, appUser.user_id]);

  const startSpeechRecognizing = useCallback(
    async (defaultValue = '') => {
      accumulatedSpeechToTextValue.current = defaultValue;

      try {
        await initializeRecognizer();
      } catch (error) {
        logger.warn('Failed to initialize speech recognizing', error);
        return;
      }

      let currentSessionId = '';
      recognizer.current!.sessionStarted = (_, e) => {
        currentSessionId = e.sessionId;
      };

      recognizer!.current!.recognizing = (
        _: Recognizer,
        event: SpeechRecognitionEventArgs,
      ) => {
        if (!event.result?.text || event.sessionId !== currentSessionId) {
          return;
        }
        setVoiceDetected(true);

        switch (event.result.reason) {
          case ResultReason.RecognizingSpeech:
            onRecognizing(
              `${accumulatedSpeechToTextValue.current} ${event.result.text}`.trim(),
            );
            break;
          default:
            log.error(
              'Unexpected Microsoft TTS recognizing result: ',
              event.result.reason,
            );
            break;
        }
      };

      recognizer!.current!.recognized = async (
        _: Recognizer,
        event: SpeechRecognitionEventArgs,
      ) => {
        if (!event.result?.text || event.sessionId !== currentSessionId) {
          return;
        }

        setDetectedLanguage(event.result.language);

        logAction('speech_recognized', {
          wordsCount: event.result.text?.split(' ').length,
          language: event.result.language,
        });
        setVoiceDetected(false);
        switch (event.result.reason) {
          case ResultReason.RecognizedSpeech:
            /**
             *
             * CE Requires text to be in english.
             * When user speaks in another language we get text in that language.
             * Here we translate that text into English so that it can be sent to CE.
             */
            if (event.result.language !== 'en-US') {
              const translationsMap = await translateText(event.result.text, [
                'en-US',
              ]);
              const translatedText = translationsMap
                .get(event.result.text)
                ?.get('en');

              if (translatedText) {
                accumulatedSpeechToTextValue.current =
                  `${accumulatedSpeechToTextValue.current} ${translatedText}`.trim();
              }
            } else {
              accumulatedSpeechToTextValue.current =
                `${accumulatedSpeechToTextValue.current} ${event.result.text}`.trim();
            }

            onRecognizing(accumulatedSpeechToTextValue.current);

            break;
          default:
            log.error(
              'Unexpected Microsoft TTS recognized result: ',
              event.result.reason,
            );
            break;
        }
      };

      setRecordInProgress(true);
      recordInProgressRef.current = true;

      recognizer.current?.startContinuousRecognitionAsync();
    },
    [
      initializeRecognizer,
      onRecognizing,
      setDetectedLanguage,
      setRecordInProgress,
    ],
  );

  const stopSpeechRecognizing = useCallback(async () => {
    mediaStream.current?.getAudioTracks().forEach((track) => track.stop());

    setVoiceDetected(false);
    setRecordInProgress(false);
    recordInProgressRef.current = false;

    if (!recognizer.current) {
      return;
    }

    await new Promise<void>((resolve) => {
      if (!recognizer.current) {
        resolve();
        return;
      }

      recognizer.current?.stopContinuousRecognitionAsync(
        () => resolve(),
        () => resolve(),
      );
    });

    recognizer.current?.close();
    audioConfig.current?.close();

    recognizer.current = undefined;
    audioConfig.current = undefined;
  }, [setRecordInProgress]);

  return useMemo(
    () => ({
      recordInProgressRef,
      voiceDetected,
      startSpeechRecognizing,
      stopSpeechRecognizing,
    }),
    [voiceDetected, startSpeechRecognizing, stopSpeechRecognizing],
  );
};
